import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useAuth } from './hooks/useAuth';
import { useAutomationRules } from './hooks/useAutomationRules';
import { useCatalog } from './hooks/useCatalog';
import { useNewAutomationRule } from './hooks/useNewAutomationRule';
import { useNewPlaybook } from './hooks/useNewPlaybook';
import { usePlaybooks } from './hooks/usePlaybooks';

type AuthHook = ReturnType<typeof useAuth>;
type CatalogHook = ReturnType<typeof useCatalog>;
type PlaybooksHook = ReturnType<typeof usePlaybooks>;
type AutomationRulesHook = ReturnType<typeof useAutomationRules>;
type NewAutomationRuleHook = ReturnType<typeof useNewAutomationRule>;
type NewPlaybookHook = ReturnType<typeof useNewPlaybook>;

interface SoarStoreContextState {
  auth: AuthHook;
  catalog: CatalogHook;
  playbooks: PlaybooksHook;
  automationRules: AutomationRulesHook;
  newAutomationRule: NewAutomationRuleHook;
  newPlaybook: NewPlaybookHook;
}

const SoarStoreContext = createContext<SoarStoreContextState | undefined>(undefined);

export const SoarStoreProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const catalog = useCatalog();
  const playbooks = usePlaybooks();

  // We pass callbacks to sync the list stores when a new item is saved
  const automationRules = useAutomationRules();

  const onAutomationRuleSaved = useCallback(
    (rule: any, isNew: boolean) => {
      if (isNew) {
        automationRules.addRule(rule);
      } else {
        automationRules.updateRuleInList(rule.id, rule);
      }
    },
    [automationRules.addRule, automationRules.updateRuleInList]
  );

  const newAutomationRule = useNewAutomationRule(onAutomationRuleSaved);

  const onPlaybookSaved = useCallback(
    (playbook: any, isNew: boolean) => {
      if (isNew) {
        playbooks.addPlaybook(playbook);
      } else {
        playbooks.updatePlaybook(playbook.id, playbook);
      }
    },
    [playbooks.addPlaybook, playbooks.updatePlaybook]
  );

  const newPlaybook = useNewPlaybook(onPlaybookSaved, catalog.actions);

  const value = useMemo(
    () => ({
      auth,
      catalog,
      playbooks,
      automationRules,
      newAutomationRule,
      newPlaybook,
    }),
    [auth, catalog, playbooks, automationRules, newAutomationRule, newPlaybook]
  );

  return <SoarStoreContext.Provider value={value}>{children}</SoarStoreContext.Provider>;
};

export const useSoarStore = () => {
  const context = useContext(SoarStoreContext);
  if (context === undefined) {
    throw new Error('useSoarStore must be used within a SoarStoreProvider');
  }
  return context;
};
